<script setup>
import { ref, watch, computed } from 'vue';
import Modal from './Modal.vue';
import MovieDetailsForm from './MovieDetailsForm.vue';
import Tabs from './Tabs.vue';
import FormGroup from './FormGroup.vue';

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  mode: { type: String, default: '' },
  type: { type: String, default: '' },
  entity: { type: Object, default: null },
  parent: { type: Object, default: null },
  parentMovie: { type: Object, default: null },
  relationName: { type: String, default: '' },
  onSubmit: { type: Function, default: null },
  schema: { type: Object, required: true }
});
const emit = defineEmits(['close', 'submit']);

const tab = ref('create');
const selectedId = ref('');
const availableEntities = ref([]);
const formEntity = ref(props.entity ? { ...props.entity } : {});
const jobId = ref(null);

function updateAvailableEntities() {
  if (props.mode === 'add' && props.type && props.schema[props.type]) {
    const movieSource = props.parentMovie || {};
    const all = Array.isArray(movieSource[props.type]) ? movieSource[props.type] : [];
    availableEntities.value = all;
  } else {
    availableEntities.value = [];
  }
  selectedId.value = '';
}

watch(() => [props.isOpen, props.mode, props.type, props.parent, props.parentMovie], updateAvailableEntities, { immediate: true });
watch(
  () => props.entity,
  (val) => {
    formEntity.value = val ? { ...val } : {};
  },
  { immediate: true, deep: true }
);

function handleTab(tabName) {
  tab.value = tabName;
}

function handleFormSubmit(data) {
  emit('submit', data);
}

function handleAddExisting() {
  if (!selectedId.value) return;
  emit('submit', { selectedId: selectedId.value, mode: 'select', type: props.type, relationName: props.relationName });
}

function handleJobComplete() {
  emit('close');
}

const modalTitle = computed(() => {
  if (!props.mode) return '';
  return (props.mode === 'edit' ? 'Edit ' : 'Add ') + props.type;
});
</script>
<template>
  <Modal :is-open="isOpen" :title="modalTitle" @close="$emit('close')">
    <template v-if="mode === 'add' && type && schema[type] && parent && parentMovie">
      <Tabs
        v-model="tab"
        :tabs="[
          { label: 'Create New', value: 'create' },
          { label: 'Select Existing', value: 'select' }
        ]"
      />
      <div v-if="tab !== 'select'">
        <MovieDetailsForm :entity-type="type" :values="formEntity" :schema="schema" :submit-text="'Create'" @submit="handleFormSubmit" />
      </div>
      <div v-else>
        <FormGroup>
          <select v-model="selectedId">
            <option value="">Select...</option>
            <option v-for="entity in availableEntities" :key="entity.id" :value="entity.id">
              {{
                schema[type].fields
                  .map((f) => entity[f.name])
                  .filter(Boolean)
                  .join(' | ') || entity.id
              }}
            </option>
          </select>
        </FormGroup>
        <div class="form-actions">
          <button :disabled="!selectedId" @click="handleAddExisting">Use selected {{ type }}</button>
        </div>
      </div>
    </template>
    <template v-else>
      <MovieDetailsForm v-if="mode" :entity-type="type" :values="formEntity" :schema="schema" :submit-text="mode === 'edit' ? 'Update' : 'Create'" @submit="handleFormSubmit" />
    </template>
  </Modal>
</template>
